/* Next */
import Link from 'next/link'
import { useRouter } from 'next/router'

/* Material */
import MuiBreadcrumbs from '@mui/material/Breadcrumbs'
import MuiLink from '@mui/material/Link'
import Typography from '@mui/material/Typography'

/* Schema */
import { BreadcrumbList, WithContext } from 'schema-dts'
import { JsonLd } from 'src/components/JsonLd'

/* Types */
import type { BreadcrumbItem } from 'src/shared/frontendTypes/product'

/* Config */
import shopInfos from 'template/infos'

const shopUrl = shopInfos.url.replace(/\/$/, '')

const normalizeHref = (href?: string) => {
  if (!href) return undefined
  if (/^https?:\/\//i.test(href)) return href
  return href.startsWith('/') ? href : `/${href}`
}

const absoluteHref = (href?: string) => {
  const normalizedHref = normalizeHref(href)
  if (!normalizedHref) return undefined
  if (/^https?:\/\//i.test(normalizedHref)) return normalizedHref
  return `${shopUrl}${normalizedHref}`
}

const currentPath = (asPath?: string) => {
  const path = String(asPath || '').split(/[?#]/, 1)[0]
  return normalizeHref(path)
}

const cleanupItems = (items: BreadcrumbItem[]) => {
  const normalizedItems = items
    .map(item => ({
      title: String(item.title || '').trim(),
      href: normalizeHref(item.href),
    }))
    .filter(item => item.title && item.title !== '0')

  const withHome = normalizedItems[0]?.href === '/'
    ? normalizedItems
    : [{ title: 'Home', href: '/' }, ...normalizedItems]

  return withHome.filter((item, index, list) => {
    const previous = list[index - 1]
    return !previous || previous.title !== item.title || previous.href !== item.href
  })
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[]
  className?: string
}

export const buildSeoBreadcrumbJsonLd = (
  items: BreadcrumbItem[] = [],
  asPath?: string,
): WithContext<BreadcrumbList> | null => {
  const crumbs = cleanupItems(items)
  if (crumbs.length < 2) return null

  const itemListElement = crumbs.reduce<Array<{
    '@type': 'ListItem'
    position: number
    name: string
    item: string
  }>>((result, crumb, index) => {
    const href = absoluteHref(
      crumb.href || (index === crumbs.length - 1 ? currentPath(asPath) : undefined),
    )

    // A menu container without a destination is useful in the visible trail,
    // but it is not a valid BreadcrumbList entry for Google.
    if (!href) return result

    result.push({
      '@type': 'ListItem',
      position: result.length + 1,
      name: crumb.title,
      item: href,
    })
    return result
  }, [])

  if (itemListElement.length < 2) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  } as WithContext<BreadcrumbList>
}

export const SeoBreadcrumbJsonLd: React.FC<BreadcrumbProps> = ({ items = [] }) => {
  const { asPath } = useRouter()
  const item = buildSeoBreadcrumbJsonLd(items, asPath)
  if (!item) return null

  return <JsonLd<BreadcrumbList> item={item} />
}

export const BreadcrumbTrail: React.FC<BreadcrumbProps> = ({
  items = [],
  className,
}) => {
  const crumbs = cleanupItems(items)
  if (crumbs.length < 2) return null

  return (
    <MuiBreadcrumbs
      component='nav'
      aria-label='Navigationspfad'
      className={className}
      sx={{
        fontSize: 13,
        marginBottom: 2,
        maxWidth: '100%',
        overflow: 'hidden',
        '& ol': {
          alignItems: 'center',
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          flexWrap: 'nowrap',
        },
        '& li': {
          display: 'flex',
          alignItems: 'center',
          flex: '0 1 auto',
          minWidth: 0,
        },
        '& li:last-of-type': {
          flex: '1 1 auto',
          overflow: 'hidden',
        },
        '& .MuiBreadcrumbs-separator': {
          display: 'block',
          flexShrink: 0,
          marginInline: { xs: .75, sm: 1 },
        },
      }}
    >
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1

        if (isLast || !crumb.href) {
          return (
            <Typography
              key={`${crumb.title}-${index}`}
              component='span'
              color={isLast ? 'text.primary' : 'text.secondary'}
              title={isLast ? crumb.title : undefined}
              sx={{
                fontSize: 'inherit',
                ...(isLast
                  ? {
                    display: 'block',
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }
                  : {}),
              }}
            >
              {crumb.title}
            </Typography>
          )
        }

        return (
          <Link key={`${crumb.href}-${index}`} href={crumb.href} passHref legacyBehavior>
            <MuiLink
              underline='hover'
              color='inherit'
              sx={{
                display: 'block',
                fontSize: 'inherit',
                maxWidth: { xs: '32vw', sm: 180, md: 240 },
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {crumb.title}
            </MuiLink>
          </Link>
        )
      })}
    </MuiBreadcrumbs>
  )
}
